<template>
  <div class="order-list">
    <!--加载状态-->
    <div class="order-loading" v-if="!ordersLoaded">
      <div class="loading-icon"></div>
    </div>
    <!--空订单-->
    <div class="order-empty" v-if="ordersLoaded && !orders.length">
      <div class="order-empty__icon" aria-hidden="true">
        <svg viewBox="0 0 160 140" xmlns="http://www.w3.org/2000/svg">
          <!-- shelf frame -->
          <rect x="12" y="12" width="136" height="116" rx="8" fill="none" stroke="#d8d8de" stroke-width="2"/>
          <!-- shelves -->
          <line x1="14" y1="58" x2="146" y2="58" stroke="#d8d8de" stroke-width="2"/>
          <line x1="14" y1="100" x2="146" y2="100" stroke="#d8d8de" stroke-width="2"/>
          <!-- top shelf: one accent book + one soft book to hint future fill -->
          <rect x="30" y="24" width="14" height="32" rx="2" fill="#f56368"/>
          <rect x="48" y="28" width="11" height="28" rx="2" fill="#fde6e7" stroke="#f3c2c4" stroke-width="1"/>
          <!-- empty dashed slot suggesting "your next one here" -->
          <rect x="64" y="26" width="12" height="30" rx="2" fill="none" stroke="#d8d8de" stroke-width="1.5" stroke-dasharray="3 3"/>
          <!-- middle shelf: completely empty (intentional) -->
        </svg>
      </div>
      <h2 class="order-empty__title">{{ $t('orders.tipEmpty') }}</h2>
      <p class="order-empty__sub">{{ $t('orders.emptySub') }}</p>
      <router-link
        :to="{ name: 'products', query: { type: 'diarybook' } }"
        class="order-empty__cta"
      >
        {{ $t('orders.emptyCta') }}
      </router-link>
    </div>
    <!--订单列表-->
    <div class="order-list-inner" v-if="ordersLoaded && orders.length">
      <order-item v-for="(order, index) in orders" :key="index"
                  :order="order"
                  @check="checkOrder(order)" @delete="deleteOrder(order.orderId)"/>
    </div>
    <popup :show="showDetail" @close="showDetail=false">
      <order-info slot="body" :order="activeOrderInfo"></order-info>
    </popup>
  </div>
</template>

<script>
import api from 'src/api/'
import OrderItem from './OrderItem.vue'
import { mapGetters, mapActions } from 'vuex'
import OrderInfo from './OrderInfo.vue'
import { findIndex } from 'lodash'
import generateCover from '../../_utils/generateCover'

export default {
  name: 'order-list',
  components: { OrderItem, OrderInfo },
  data () {
    return {
      orders: [],
      ordersLoaded: false,
      showDetail: false,
      activeOrderInfo: {}
    }
  },
  async created () {
    // After Stripe Checkout payment, verify first, then load orders
    const payment = this.$route.query.payment
    const sessionId = this.$route.query.session_id
    const orderId = this.$route.query.order_id
    if (payment === 'success' && sessionId) {
      await this.verifyStripePayment(sessionId, orderId)
    }
    this.fetchOrders(this.user.uid)
  },
  computed: {
    ...mapGetters([
      'user'
    ])
  },
  methods: {
    ...mapActions([
      'setLoading'
    ]),
    async verifyStripePayment (sessionId, orderId) {
      try {
        this.setLoading({ text: this.$t('orderStatus.verifying'), visible: true })
        const data = await api.verifyStripeCheckout(sessionId, orderId)
        this.setLoading(false)
        if (data && data.paid) {
          this.$toast({
            message: this.$t('orderStatus.successTitle'),
            icon: 'fa-check-circle',
            duration: 3000
          })
        } else if (data && data.errors) {
          this.$toast({
            message: this.$t('orderStatus.failedTitle'),
            position: 'bottom'
          })
        }
      } catch (e) {
        this.setLoading(false)
        console.error('Stripe verify error:', e)
      }
      // Clean up URL params
      if (this.$route.query.payment) {
        this.$router.replace({ path: this.$route.path })
      }
    },
    async fetchOrders (uid) {
      try {
        const data = await api.fetchOrders(uid)
        if (!data || data.errors) {
          this.$toast({
            message: this.$t('orders.tipLoadFailed'),
            position: 'bottom'
          })
          return
        }
        this.orders = data.map(order => {
          const {
            book,
            bookType
          } = order
          const {
            design,
            cover,
            toDate,
            fromDate,
            templateStyle
          } = book

          book.type = bookType
          if (bookType === 'calendar') {
            if (!book.totalPage) {
              book.totalPage = 13
            }
            if (!design) {
              book.design = {
                ...cover,
                cover: cover,
                template: cover.coverTemplate
              }
            }
          }

          const chapters = [{ month: fromDate }, { month: toDate }]
          book.coverUrl = generateCover({
            design: book.design,
            chapters,
            isCalendar: bookType === 'calendar'
          })
          let style
          switch (bookType) {
            case 'photobook':
              style = '簡約版'
              if (templateStyle === 'magazine-1' && templateStyle.includes('travel')) {
                style = '旅行風'
              }
              if (templateStyle.includes('love')) {
                style = '爱情風'
              }
              if (templateStyle.includes('japan')) {
                style = '日系風'
              }
              if (templateStyle.includes('child')) {
                style = '童趣版'
              }
              if (templateStyle.includes('baby')) {
                style = '幼兒版'
              }
              book.spoiler = style
              if (book.template === 'album') {
                book.spoiler = 'A4雜誌冊'
              }
              break
            case 'calendar':
              book.spoiler = '簡約版月曆'
              break
          }
          return order
        })
      } finally {
        this.ordersLoaded = true
      }
    },
    checkOrder (order) {
      this.showDetail = true
      this.activeOrderInfo = order
    },
    deleteOrder (orderId) {
      this.$confirm({
        title: this.$t('orders.labelDeleteOrder'),
        okText: this.$t('button.delete'),
        cancelText: this.$t('button.cancel'),
        okClass: 'text-red',
        cancelClass: 'text-gray',
        onOk: (modal) => {
          modal.close()
          this.setLoading({
            text: this.$t('orders.labelDeleteOrderLoading'),
            visible: true
          })
          api.deleteOrder(orderId).then(data => {
            this.setLoading(false)
            if (!data || data.errors) {
              this.$toast({
                message: this.$t('orders.labelDeleteOrderFailed'),
                position: 'bottom'
              })
              return
            }
            const currentIndex = findIndex(this.orders, { orderId })
            this.orders.splice(currentIndex, 1)
          })
        }
      })
    }
  }
}
</script>

<style lang="scss">
.order-list {
  margin-top: 2rem;
  padding: 0 1rem;

  .order-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }

  .order-empty {
    max-width: 480px;
    margin: 0 auto;
    padding: 80px 20px 100px;
    text-align: center;

    &__icon {
      width: 160px;
      height: 140px;
      margin: 0 auto 28px;

      svg {
        width: 100%;
        height: 100%;
        display: block;
      }
    }

    &__title {
      font-size: 22px;
      font-weight: 600;
      color: #1a1a1a;
      margin: 0 0 12px;
    }

    &__sub {
      font-size: 15px;
      line-height: 1.6;
      color: #888;
      margin: 0 0 32px;
    }

    &__cta {
      display: inline-block;
      padding: 12px 36px;
      background: #f56368;
      color: #fff;
      border-radius: 24px;
      font-size: 16px;
      font-weight: 500;
      text-decoration: none;
      box-shadow: 0 2px 8px rgba(245, 99, 104, 0.3);
      transition: transform .2s ease, box-shadow .2s ease, background .2s ease;

      &:hover {
        transform: translateY(-1px);
        background: #ff7478;
        box-shadow: 0 6px 16px rgba(245, 99, 104, 0.4);
        color: #fff;
      }

      &:active {
        transform: translateY(0);
      }
    }

    @media (max-width: 620px) {
      padding: 56px 20px 72px;

      &__icon {
        width: 128px;
        height: 112px;
        margin-bottom: 20px;
      }
      &__title { font-size: 19px; }
      &__sub { font-size: 14px; margin-bottom: 24px; }
    }
  }

  .order-list-inner {
    max-width: 640px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;

    > .order-item + .order-item::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      border-top: 1px solid #ddd;
      transition: opacity 0.2s;
    }

    > .order-item:hover::before,
    > .order-item:hover + .order-item::before {
      opacity: 0;
    }
  }

  .popup-content {
    @media (max-width: 767px) {
      top: 0;
    }
  }
}
</style>
